import { useEffect, useRef, useState, type ReactNode } from "react";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { Drawer } from "../../Drawer";
import { TesisPickerField, useTesisPicker, resolveTesisId } from "../../ui/TesisPickerField";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../../components/mobile/primitives";
// dt2 / badge-pill / cell-num / empty-state design-system tablo sınıfları global stile değil
// bu sayfa CSS'ine tanımlı → sekme tek dosya olduğundan buradan içeri alınır (Vite global bundle'lar).
import "../../../pages/Items.css";

/**
 * STEPERP — İnsan Kaynakları / Bordro sekmesi (Faz 8).
 *
 * YALNIZ SÜPER-ADMIN: bordro/maaş finansal veri; her uç sunucuda assertSalaryAccess(ctx)
 * ile korunur. isSuper=false ise sekme salt "erişim kısıtlı" boş durumu gösterir.
 * Dönem kodu BRD-YYYY-MM. Tüm tutarlar API'den Number gelir; mono + tabular-nums gösterilir.
 */

type PayrollStatus = "draft" | "calculated" | "approved" | "paid" | "cancelled";

const STATUS_META: Record<PayrollStatus, { cls: string; label: string; icon: string }> = {
  draft: { cls: "b-warn", label: "Taslak", icon: "fileText" },
  calculated: { cls: "b-info", label: "Hesaplandı", icon: "chart" },
  approved: { cls: "b-success", label: "Onaylandı", icon: "check" },
  paid: { cls: "b-primary", label: "Ödendi", icon: "banknote" },
  cancelled: { cls: "", label: "İptal", icon: "x" },
};

/** Bu durumlarda dönem hesaplanabilir ve kalemler elle düzenlenebilir (sunucu da aynı kuralı uygular). */
const EDITABLE = new Set<PayrollStatus>(["draft", "calculated"]);

const MONTHS_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

const CUR_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

const fmtNum = (n: number) =>
  new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);
const fmtNum1 = (n: number) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(n ?? 0);
const fmtMoney = (n: number, cur: string = "TRY") => `${fmtNum(n)} ${CUR_SYMBOL[cur] ?? cur}`;
const fmtDate = (d: string | Date | null | undefined) =>
  d ? new Date(d).toLocaleDateString("tr-TR") : "—";
const periodLabel = (y: number, m: number) => `${MONTHS_TR[m - 1] ?? m} ${y}`;

/** Boş/negatif/NaN → null (geçersiz). Aksi halde >= 0 sayı. */
function numOr(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const v = Number(t);
  return Number.isFinite(v) && v >= 0 ? v : null;
}

type EditRow = { id: string; grossPay: string; allowances: string; deductions: string };

export function PayrollTab({ canManage, isSuper }: { canManage: boolean; isSuper: boolean }) {
  const qc = trpc.useUtils();
  const isMobile = useIsMobile();
  // Sekme yalnız süper-admine açık; sunucu da super-admin şartı koşar → yazma yetkisi = isSuper.
  const canWrite = isSuper || canManage;
  // Süper-admin "Tüm Tesisler"deyken bordro dönemi hangi tesise ait olmalı — açıkça seçtir
  // (aksi halde run.tesisId=null olur ve hesaplama o tesisin personelini bulamaz).
  const tp = useTesisPicker(true);
  const [newTesis, setNewTesis] = useState("");

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) =>
    setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<EditRow | null>(null);
  const [confirm, setConfirm] = useState<null | "approve" | "delete">(null);
  /** Ön izleme sonucu — KAYDEDİLMEMİŞ rakamlar. Dolu oldukça tablo bunu gösterir. */
  const [preview, setPreview] = useState<null | Array<{
    employeeId: string; employeeName: string; grossPay: number; allowances: number; deductions: number;
    basisDays: number; workedDays: number; absentDays: number; paidLeaveDays: number; unpaidLeaveDays: number;
    overtimeHours: number; dailyRate: number; hourlyRate: number; absenceDeduction: number;
    unpaidLeaveDeduction: number; overtimePay: number; netPay: number; currency: string;
    overtimeRateSource: string | null;
  }>>(null);

  // Dönem oluştur formu
  const now = new Date();
  const [createOpen, setCreateOpen] = useState(false);
  const [year, setYear] = useState<string>(String(now.getFullYear()));
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [note, setNote] = useState("");

  const listQ = trpc.hr.payroll.list.useQuery(undefined, { staleTime: 5_000, enabled: isSuper });
  const getQ = trpc.hr.payroll.get.useQuery(
    { id: detailId ?? "" },
    { enabled: isSuper && !!detailId, staleTime: 3_000 }
  );
  const run = getQ.data ?? null;

  const invalidate = () => qc.hr.payroll.invalidate();

  const createMut = trpc.hr.payroll.create.useMutation({
    onSuccess: (res) => {
      invalidate();
      setCreateOpen(false);
      pushToast({ tone: "success", message: `${res.code ?? "Bordro"} dönemi oluşturuldu.` });
      setDetailId(res.id); // yeni dönemi aç → kullanıcı hemen "Hesapla" diyebilsin
    },
    onError: (e) => pushToast({ tone: "danger", title: "Oluşturulamadı", message: e.message }),
  });
  const calculateMut = trpc.hr.payroll.calculate.useMutation({
    onSuccess: (res) => {
      if (res.preview) {
        setPreview(res.items);
        pushToast({ tone: "info", title: "Ön izleme", message: "Hiçbir şey kaydedilmedi — rakamlar yalnız ekranda." });
        return;
      }
      invalidate();
      setPreview(null);
      // Uyarılar okunmadan kaybolmasın; 200 personelde liste ekranı taşabilir → ilk 8.
      const WMAX = 8;
      const lines = res.warnings.map((w) => (w.days && w.days > 1 ? `${w.message} (${w.days})` : w.message));
      pushToast({
        tone: res.warnings.length ? "warning" : "success",
        title: `Hesaplandı — ${res.itemCount} personel kalemi`,
        message: res.warnings.length ? `${res.warnings.length} uyarı` : undefined,
        duration: res.warnings.length ? 0 : undefined,
        lines: lines.length > WMAX ? [...lines.slice(0, WMAX), `… ve ${lines.length - WMAX} uyarı daha`] : lines,
      });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Hesaplanamadı", message: e.message }),
  });
  const updateItemMut = trpc.hr.payroll.item.update.useMutation({
    onSuccess: () => {
      invalidate();
      setEditRow(null);
      pushToast({ tone: "success", message: "Kalem güncellendi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Güncellenemedi", message: e.message }),
  });
  const approveMut = trpc.hr.payroll.approve.useMutation({
    onSuccess: () => {
      invalidate();
      setConfirm(null);
      pushToast({ tone: "success", message: "Bordro onaylandı." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Onaylanamadı", message: e.message }),
  });
  const removeMut = trpc.hr.payroll.remove.useMutation({
    onSuccess: () => {
      invalidate();
      setConfirm(null);
      closeDetail();
      pushToast({ tone: "success", message: "Bordro dönemi silindi." });
    },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message }),
  });

  function closeDetail() {
    setDetailId(null);
    setEditRow(null);
    setConfirm(null);
  }
  // Dönem değişince satır düzenlemesini VE ön izlemeyi sıfırla.
  // Ön izleme temizlenmezse: Mayıs'ı ön izle → kapat → Haziran'ı aç → tablo MAYIS'ın
  // kaydedilmemiş satırlarını Haziran başlığı altında gösterir.
  useEffect(() => {
    setEditRow(null);
    setPreview(null);
  }, [detailId]);

  function openCreate() {
    setYear(String(new Date().getFullYear()));
    setMonth(new Date().getMonth() + 1);
    setNote("");
    setNewTesis("");
    setCreateOpen(true);
  }

  const yNum = Number(year);
  // Süper-admin "Tüm Tesisler"deyse tesis seçimi zorunlu (aksi halde boş bordro).
  const createValid = Number.isInteger(yNum) && yNum >= 2000 && yNum <= 2100 && month >= 1 && month <= 12 && (!tp.show || !!newTesis);
  function submitCreate() {
    if (!createValid) return;
    createMut.mutate({ periodYear: yNum, periodMonth: month, note: note.trim() || null, tesisId: resolveTesisId(tp.show, newTesis) });
  }

  const editValid =
    !!editRow &&
    numOr(editRow.grossPay) !== null &&
    numOr(editRow.allowances) !== null &&
    numOr(editRow.deductions) !== null;
  /**
   * Düzenleme satırındaki canlı "Net" — sunucunun YAZACAĞI değerle aynı olmalı.
   * Eski hâli motorun üç kalemini (devamsızlık/ücretsiz izin/mesai) atlıyordu: ekran
   * 30.000 gösterirken sunucu 29.000 kaydediyordu ve kullanıcı yanlış rakama bakarak
   * para kararı veriyordu. Brüt değişirse oranlar da değişeceğinden kesintiler brüt
   * oranında ölçeklenir (sunucu da aynı `dailyRate × gün` hesabını yapar).
   */
  const editBase = editRow ? (run?.items.find((i) => i.id === editRow.id) ?? null) : null;
  const editNet = (() => {
    if (!editRow) return 0;
    const g = numOr(editRow.grossPay) ?? 0;
    const a = numOr(editRow.allowances) ?? 0;
    const d = numOr(editRow.deductions) ?? 0;
    if (!editBase) return g + a - d;
    // Oran = yeni brüt / eski brüt (eski brüt 0 ise kesinti de 0'dı, ölçek anlamsız).
    const k = editBase.grossPay > 0 ? g / editBase.grossPay : 0;
    const absence = editBase.absenceDeduction * k;
    const unpaid = editBase.unpaidLeaveDeduction * k;
    // Mesai ücreti brüte YALNIZ oran brütten türetildiğinde bağlıdır. Pozisyon/kişi oranı
    // kullanıldıysa (kaynak dolu) brüt değişse de mesai ücreti SABİT kalır — sunucu da
    // dondurulmuş oranı geri besleyerek aynısını yapar (payroll.ts item.update).
    const ot = editBase.overtimeRateSource ? editBase.overtimePay : editBase.overtimePay * k;
    return Math.round((g - absence - unpaid + ot + a - d) * 100) / 100;
  })();
  function saveEdit() {
    if (!editRow || !editValid) return;
    const g = numOr(editRow.grossPay)!;
    // `grossPay` YALNIZ gerçekten değiştiyse gönderilir: sunucu her gönderimde
    // `manualGross` damgalıyor → sırf ek ödeme eklemek için formu açan kullanıcı
    // o satırın brütünü kalıcı olarak donduruyordu.
    const grossChanged = !editBase || g !== editBase.grossPay;
    updateItemMut.mutate({
      id: editRow.id,
      ...(grossChanged ? { grossPay: g } : {}),
      allowances: numOr(editRow.allowances)!,
      deductions: numOr(editRow.deductions)!,
    });
  }

  // ── isSuper değilse: erişim kısıtlı boş durumu ──
  if (!isSuper) {
    return (
      <div className="empty-state">
        <span className="ic" aria-hidden="true"><Icon name="banknote" size={30} /></span>
        <p className="ttl">Bordro erişimi kısıtlı</p>
        <p className="sub">Bordro ve maaş verileri yalnızca sistem yöneticisi tarafından görüntülenebilir.</p>
      </div>
    );
  }

  const runs = listQ.data ?? [];
  const editable = !!run && canWrite && EDITABLE.has(run.status as PayrollStatus);
  // `run.items[0].currency` TAHMİNİ kaldırıldı: karışık para biriminde ilk kalemin birimiyle
  // TRY+USD toplamı basılıyordu. Toplamlar artık para birimi BAZINDA geliyor.

  /**
   * Tabloda gösterilen satırlar: ön izleme açıksa KAYDEDİLMEMİŞ hesap sonucu, aksi hâlde
   * kayıtlı kalemler. Ön izleme satırlarının `id`si yoktur (DB'de karşılığı yok) — sahte
   * bir anahtar verilir ve düzenleme kapatılır.
   */
  // Mesai ücreti iki farklı tabandan çıkabilir; hangisinin kullanıldığı GÖRÜNÜR olmalı,
  // yoksa "pozisyona ücret girdim ama uygulanmamış" hatası fark edilmeden ödenir.
  const OT_SOURCE_SHORT: Record<string, string> = { position: "pozisyon oranı", employee: "kişiye özel" };
  const otRateTitle = (it: { overtimeRateSource: string | null; hourlyRate: number; overtimeHours: number }): string | undefined => {
    if (!it.overtimeHours) return undefined;
    return it.overtimeRateSource
      ? `Mesai saat ücreti ${fmtNum(it.hourlyRate)} (${OT_SOURCE_SHORT[it.overtimeRateSource] ?? it.overtimeRateSource}) × ${fmtNum1(it.overtimeHours)} sa — katsayı uygulanmaz`
      : `Saatlik ücret ${fmtNum(it.hourlyRate)} (brütten türetildi) × katsayı × ${fmtNum1(it.overtimeHours)} sa`;
  };

  const viewItems: Array<{
    id: string; employeeId: string; employeeName: string; employeeCode: string | null;
    grossPay: number; allowances: number; deductions: number; netPay: number;
    basisDays: number; workedDays: number; absentDays: number; paidLeaveDays: number; unpaidLeaveDays: number;
    overtimeHours: number; dailyRate: number; hourlyRate: number;
    absenceDeduction: number; unpaidLeaveDeduction: number; overtimePay: number;
    /** null = oran brütten türetildi (katsayı UYGULANDI); dolu = oran doğrudan girilmiş (katsayı YOK). */
    overtimeRateSource: string | null;
    manualGross: boolean; currency: string; note: string | null;
  }> = preview
    ? preview.map((p) => ({ ...p, id: `preview-${p.employeeId}`, employeeCode: null, manualGross: false, note: null }))
    : (run?.items ?? []);

  const stat = (label: string, value: ReactNode, mono = true) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 11, color: "var(--neutral-500)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</span>
      <span style={{ fontFamily: mono ? "var(--font-mono)" : "inherit", fontVariantNumeric: "tabular-nums", fontSize: 13, fontWeight: 600, color: "var(--neutral-900)" }}>{value}</span>
    </div>
  );

  const detailFooter = (
    <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
      {run && canWrite && (run.status === "draft" || run.status === "calculated") ? (
        <>
          {/* Ön izleme YAZMAZ — rakamları görüp karar vermek için. */}
          <button type="button" className="btn-md ghost"
            onClick={() => calculateMut.mutate({ id: run.id, preview: true })} disabled={calculateMut.isPending}>
            <Icon name="eye" size={14} />Ön İzleme
          </button>
          <button type="button" className={`btn-md ${run.status === "draft" ? "primary" : "secondary"}`}
            onClick={() => calculateMut.mutate({ id: run.id })} disabled={calculateMut.isPending}>
            <Icon name="refresh" size={14} />
            {calculateMut.isPending ? "Hesaplanıyor…" : run.status === "draft" ? "Hesapla" : "Yeniden Hesapla"}
          </button>
        </>
      ) : null}
      {run && canWrite && run.status === "calculated" ? (
        <button type="button" className="btn-md primary" onClick={() => setConfirm("approve")}>
          <Icon name="check" size={14} />Onayla
        </button>
      ) : null}
      {run && canWrite && (run.status === "draft" || run.status === "cancelled") ? (
        <button type="button" className="btn-md danger-outline" onClick={() => setConfirm("delete")}>
          <Icon name="trash" size={14} />Sil
        </button>
      ) : null}
      <div style={{ flex: 1 }} />
      <button type="button" className="btn-md ghost" onClick={closeDetail}>Kapat</button>
    </div>
  );

  const drawers = (
    <>
      {/* ── Dönem Oluştur ── */}
      <Drawer
        open={createOpen} onClose={() => setCreateOpen(false)} size="md" dismissible={false}
        title="Bordro Dönemi Oluştur" subtitle="Kod otomatik üretilir (BRD-YYYY-MM)"
        footer={
          <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
            <button type="button" className="btn-md primary" onClick={submitCreate} disabled={!createValid || createMut.isPending}>
              {createMut.isPending ? "Oluşturuluyor…" : "Oluştur"}
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-md ghost" onClick={() => setCreateOpen(false)}>Vazgeç</button>
          </div>
        }
      >
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="pr-year">Yıl <span className="req">*</span></label>
            <input id="pr-year" className="form-input" type="number" min={2000} max={2100}
              value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026" autoFocus />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="pr-month">Ay <span className="req">*</span></label>
            <select id="pr-month" className="form-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS_TR.map((mn, i) => (
                <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, "0")} · {mn}</option>
              ))}
            </select>
          </div>
        </div>
        {tp.show ? (
          <div className="form-field">
            <label className="lbl">Tesis <span className="req">*</span></label>
            <TesisPickerField show={tp.show} facilities={tp.facilities} value={newTesis} onChange={setNewTesis} />
            <span className="help">Bordro bu tesisin aktif personeli için hesaplanır.</span>
          </div>
        ) : null}
        <div className="form-field">
          <label className="lbl" htmlFor="pr-note">Not</label>
          <textarea id="pr-note" className="form-textarea" rows={3} value={note}
            onChange={(e) => setNote(e.target.value)} placeholder="İsteğe bağlı açıklama" />
        </div>
        {!createValid ? <span className="help">Geçerli bir yıl (2000–2100) ve ay seçin.</span> : null}
      </Drawer>

      {/* ── Dönem Detayı ── */}
      <Drawer
        open={!!detailId} onClose={closeDetail} size="xl" dismissible={false}
        title={run ? (run.code ?? "Bordro Dönemi") : "Bordro Dönemi"}
        subtitle={run ? `${periodLabel(run.periodYear, run.periodMonth)} · ${run.tesisName ?? "Global"}` : undefined}
        footer={detailFooter}
      >
        {getQ.isLoading ? (
          <div className="empty-state"><p className="sub">Yükleniyor…</p></div>
        ) : !run ? (
          <div className="form-banner-err">Bordro dönemi bulunamadı.</div>
        ) : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center", marginBottom: 14 }}>
              <span className={`badge-pill ${STATUS_META[run.status as PayrollStatus].cls}`}>
                <Icon name={STATUS_META[run.status as PayrollStatus].icon} size={11} />
                {STATUS_META[run.status as PayrollStatus].label}
              </span>
              {stat("Dönem", periodLabel(run.periodYear, run.periodMonth), false)}
              {stat("Tesis", run.tesisName ?? "Global", false)}
              {stat("Oluşturma", fmtDate(run.createdAt), false)}
              {run.approvedAt ? stat("Onay", fmtDate(run.approvedAt), false) : null}
            </div>

            {run.note ? (
              <p style={{ fontSize: 12.5, color: "var(--neutral-600)", margin: "0 0 14px", lineHeight: 1.5 }}>{run.note}</p>
            ) : null}

            {/* Uyarı bandı — sayfa yenilense de durur (sunucu saklanmış kolonlardan türetir). */}
            {run.warnings.length ? (
              <div className="form-banner-warn" role="alert">
                <Icon name="alertTriangle" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  {run.warnings.map((w) => <div key={w.code}>{w.message}</div>)}
                </div>
              </div>
            ) : null}
            {preview ? (
              <div className="form-banner-info" role="status">
                <Icon name="info" size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                <div><b>Ön izleme</b> — bu rakamlar KAYDEDİLMEDİ. Kalıcı hâle getirmek için "Hesapla" deyin.</div>
              </div>
            ) : null}

            {/* Toplamlar PARA BİRİMİ BAZINDA — TRY ile USD toplanamaz. */}
            {run.totalsByCurrency.map((t) => (
              <div key={t.currency} className="pay-totals">
                {stat("Kalem", String(t.count))}
                {stat("Brüt", fmtMoney(t.grossPay, t.currency))}
                {stat("Devamsızlık", fmtMoney(t.absenceDeduction, t.currency))}
                {stat("Ücretsiz İzin", fmtMoney(t.unpaidLeaveDeduction, t.currency))}
                {stat("Fazla Mesai", fmtMoney(t.overtimePay, t.currency))}
                {stat("Ek Ödeme", fmtMoney(t.allowances, t.currency))}
                {stat("Kesinti", fmtMoney(t.deductions, t.currency))}
                {stat("Net", fmtMoney(t.netPay, t.currency))}
              </div>
            ))}
            {/* Hesap anındaki politika — "bu para hangi orandan çıktı" sorusunun cevabı. */}
            {run.calculatedAt ? (
              <p className="pay-policy">
                {run.dayBasis === "fixed30" ? "Bölen: sabit 30 gün" : `Bölen: ${run.calendarWorkdays} iş günü`}
                {" · "}Saatlik bölen {fmtNum1(run.overtimeDivisor ?? 0)}
                {" · "}Mesai katsayısı {fmtNum1(run.overtimeMultiplier ?? 0)}
                {" · "}Hesap {fmtDate(run.calculatedAt)}
              </p>
            ) : null}

            {viewItems.length === 0 ? (
              <div className="empty-state">
                <span className="ic" aria-hidden="true"><Icon name="users" size={28} /></span>
                <p className="ttl">Bordro kalemi yok</p>
                <p className="sub">
                  {editable
                    ? "\"Hesapla\" ile bu tesisteki aktif personellerin bordro kalemlerini üretin (maaş + puantaj)."
                    : "Bu dönemde bordro kalemi bulunmuyor."}
                </p>
              </div>
            ) : isMobile ? (
              /* Mobilde 8 kolonluk tablo 335px'e sığmaz (957px) — kendi içinde kaysa bile
                 kullanılamaz. Kart görünümü: para kalemleri etiketli, sıfırlar gizli. */
              <>
                {viewItems.map((it) => (
                  <MCard pad key={it.id} className="pay-mcard">
                    <div className="pay-mhead">
                      <span className="pay-mname">
                        <b>{it.employeeName}</b>
                        {it.employeeCode ? <small className="mono">{it.employeeCode}</small> : null}
                      </span>
                      {it.manualGross ? <span className="badge b-warning no-dot"><Icon name="edit" size={11} />elle</span> : null}
                    </div>
                    <div className="pay-mgrid">
                      <div><span className="m-ovl">Brüt</span><b className="mono">{fmtMoney(it.grossPay, it.currency)}</b></div>
                      {it.absenceDeduction ? (
                        <div><span className="m-ovl">Devamsızlık</span><b className="mono">−{fmtMoney(it.absenceDeduction, it.currency)}</b><small>{fmtNum1(it.absentDays)} gün</small></div>
                      ) : null}
                      {it.unpaidLeaveDeduction ? (
                        <div><span className="m-ovl">Ücretsiz İzin</span><b className="mono">−{fmtMoney(it.unpaidLeaveDeduction, it.currency)}</b><small>{fmtNum1(it.unpaidLeaveDays)} gün</small></div>
                      ) : null}
                      {it.overtimePay ? (
                        <div><span className="m-ovl">Fazla Mesai</span><b className="mono">+{fmtMoney(it.overtimePay, it.currency)}</b><small>{fmtNum1(it.overtimeHours)} sa{it.overtimeRateSource ? ` · ${OT_SOURCE_SHORT[it.overtimeRateSource] ?? it.overtimeRateSource}` : ""}</small></div>
                      ) : null}
                      {it.allowances ? <div><span className="m-ovl">Ek Ödeme</span><b className="mono">+{fmtMoney(it.allowances, it.currency)}</b></div> : null}
                      {it.deductions ? <div><span className="m-ovl">Kesinti</span><b className="mono">−{fmtMoney(it.deductions, it.currency)}</b></div> : null}
                    </div>
                    <div className={`pay-mnet${it.netPay < 0 ? " pay-neg" : ""}`}>
                      <span className="m-ovl">Net</span>
                      <b className="mono">{fmtMoney(it.netPay, it.currency)}</b>
                    </div>
                    {/* Mobilde satır içi düzenleme YOK (düzenleyici masaüstü tablosunda).
                        Ölü buton koymak yerine alanlar burada doğrudan düzenlenir. */}
                    {editable && !preview ? (
                      editRow?.id === it.id ? (
                        <div className="pay-medit-form">
                          <label className="form-field">
                            <span className="lbl">Brüt</span>
                            <input type="number" min="0" step="0.01" className="form-input mono" value={editRow.grossPay}
                              onChange={(e) => setEditRow((r) => r && { ...r, grossPay: e.target.value })} />
                          </label>
                          <label className="form-field">
                            <span className="lbl">Ek Ödeme</span>
                            <input type="number" min="0" step="0.01" className="form-input mono" value={editRow.allowances}
                              onChange={(e) => setEditRow((r) => r && { ...r, allowances: e.target.value })} />
                          </label>
                          <label className="form-field">
                            <span className="lbl">Kesinti</span>
                            <input type="number" min="0" step="0.01" className="form-input mono" value={editRow.deductions}
                              onChange={(e) => setEditRow((r) => r && { ...r, deductions: e.target.value })} />
                          </label>
                          <div className="pay-mnet"><span className="m-ovl">Yeni net</span><b className="mono">{fmtMoney(editNet, it.currency)}</b></div>
                          <div className="m-actions">
                            <button type="button" className="btn-md primary sm" onClick={saveEdit} disabled={!editValid || updateItemMut.isPending}>
                              <Icon name="check" size={13} />Kaydet
                            </button>
                            <button type="button" className="btn-md secondary sm" onClick={() => setEditRow(null)} disabled={updateItemMut.isPending}>
                              <Icon name="x" size={13} />Vazgeç
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button type="button" className="btn-md secondary sm pay-medit" disabled={!!editRow}
                          onClick={() => setEditRow({ id: it.id, grossPay: String(it.grossPay), allowances: String(it.allowances), deductions: String(it.deductions) })}>
                          <Icon name="edit" size={13} />Düzenle
                        </button>
                      )
                    ) : null}
                  </MCard>
                ))}
              </>
            ) : (
              <div className="dt2-scroll">
                <table className="dt2">
                  <thead>
                    <tr>
                      <th>Personel</th>
                      <th className="num" style={{ width: 132 }}>Brüt</th>
                      <th className="num" style={{ width: 128 }}>Devamsızlık</th>
                      <th className="num" style={{ width: 128 }}>Ücretsiz İzin</th>
                      <th className="num" style={{ width: 128 }}>Fazla Mesai</th>
                      <th className="num" style={{ width: 124 }}>Ek Ödeme</th>
                      <th className="num" style={{ width: 124 }}>Kesinti</th>
                      <th className="num" style={{ width: 144 }}>Net</th>
                      {editable && !preview ? <th style={{ width: 96 }}>Aksiyon</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {viewItems.map((it) => {
                      // Ön izlemede satır KAYDEDİLMEMİŞTİR → düzenlenemez.
                      const isEditing = !preview && editRow?.id === it.id;
                      if (isEditing && editRow) {
                        return (
                          <tr key={it.id}>
                            <td>
                              <div style={{ fontWeight: 600 }}>{it.employeeName}</div>
                              <div style={{ fontSize: 11, color: "var(--neutral-500)", fontFamily: "var(--font-mono)" }}>
                                {it.employeeCode}
                                {it.workedDays ? ` · ${fmtNum1(it.workedDays)} gün` : ""}
                                {it.overtimeHours ? ` · ${fmtNum1(it.overtimeHours)} sa FM` : ""}
                              </div>
                            </td>
                            <td className="cell-num">
                              <input type="number" min="0" step="0.01" className="form-input" style={{ width: 122, textAlign: "right" }}
                                value={editRow.grossPay} aria-label="Brüt"
                                onChange={(e) => setEditRow((r) => r && { ...r, grossPay: e.target.value })} />
                            </td>
                            {/* Motorun ürettiği üç kalem düzenleme sırasında SALT OKUNUR — kolon
                                sayısı başlıkla eşleşmeli, yoksa tablo kayar. */}
                            <td className="cell-num">{it.absenceDeduction ? fmtMoney(it.absenceDeduction, it.currency) : "—"}</td>
                            <td className="cell-num">{it.unpaidLeaveDeduction ? fmtMoney(it.unpaidLeaveDeduction, it.currency) : "—"}</td>
                            <td className="cell-num">{it.overtimePay ? fmtMoney(it.overtimePay, it.currency) : "—"}</td>
                            <td className="cell-num">
                              <input type="number" min="0" step="0.01" className="form-input" style={{ width: 122, textAlign: "right" }}
                                value={editRow.allowances} aria-label="Ek ödeme"
                                onChange={(e) => setEditRow((r) => r && { ...r, allowances: e.target.value })} />
                            </td>
                            <td className="cell-num">
                              <input type="number" min="0" step="0.01" className="form-input" style={{ width: 122, textAlign: "right" }}
                                value={editRow.deductions} aria-label="Kesinti"
                                onChange={(e) => setEditRow((r) => r && { ...r, deductions: e.target.value })} />
                            </td>
                            <td className="cell-num" style={{ fontWeight: 700 }}>{fmtMoney(editNet, it.currency)}</td>
                            <td>
                              <span style={{ display: "inline-flex", gap: 4 }}>
                                <button type="button" className="btn-md primary" onClick={saveEdit}
                                  disabled={!editValid || updateItemMut.isPending} aria-label="Kaydet" title="Kaydet">
                                  <Icon name="check" size={14} />
                                </button>
                                <button type="button" className="btn-md ghost" onClick={() => setEditRow(null)}
                                  disabled={updateItemMut.isPending} aria-label="Vazgeç" title="Vazgeç">
                                  <Icon name="x" size={14} />
                                </button>
                                {it.manualGross ? (
                                  <button type="button" className="btn-md ghost"
                                    onClick={() => updateItemMut.mutate({ id: it.id, manualGross: false })}
                                    disabled={updateItemMut.isPending}
                                    aria-label="Brütü otomatiğe döndür" title="Brütü otomatiğe döndür — yeniden hesapta maaş kaydından gelsin">
                                    <Icon name="refresh" size={14} />
                                  </button>
                                ) : null}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={it.id}>
                          <td>
                            <div style={{ fontWeight: 600 }}>{it.employeeName}</div>
                            <div style={{ fontSize: 11, color: "var(--neutral-500)", fontFamily: "var(--font-mono)" }}>
                              {it.employeeCode}
                              {it.workedDays ? ` · ${fmtNum1(it.workedDays)} gün` : ""}
                              {it.overtimeHours ? ` · ${fmtNum1(it.overtimeHours)} sa FM` : ""}
                            </div>
                          </td>
                          <td className="cell-num" title={it.basisDays ? `Bölen ${fmtNum1(it.basisDays)} gün · günlük ${fmtNum(it.dailyRate)} · saatlik ${fmtNum(it.hourlyRate)}` : undefined}>
                            {fmtMoney(it.grossPay, it.currency)}
                            {it.manualGross ? (
                              <span className="badge-pill b-warn pay-manual" title="Brüt elle düzeltildi — yeniden hesap bu tutarı ezmez.">
                                <Icon name="edit" size={10} />elle
                              </span>
                            ) : null}
                          </td>
                          <td className="cell-num">
                            {it.absenceDeduction ? fmtMoney(it.absenceDeduction, it.currency) : "—"}
                            {it.absentDays ? <span className="pay-sub">{fmtNum1(it.absentDays)} gün</span> : null}
                          </td>
                          <td className="cell-num">
                            {it.unpaidLeaveDeduction ? fmtMoney(it.unpaidLeaveDeduction, it.currency) : "—"}
                            {it.unpaidLeaveDays ? <span className="pay-sub">{fmtNum1(it.unpaidLeaveDays)} gün</span> : null}
                          </td>
                          <td className="cell-num" title={otRateTitle(it)}>
                            {it.overtimePay ? fmtMoney(it.overtimePay, it.currency) : "—"}
                            {it.overtimeHours ? (
                              <span className="pay-sub">
                                {fmtNum1(it.overtimeHours)} sa{it.overtimeRateSource ? ` · ${OT_SOURCE_SHORT[it.overtimeRateSource] ?? it.overtimeRateSource}` : ""}
                              </span>
                            ) : null}
                          </td>
                          <td className="cell-num">{fmtMoney(it.allowances, it.currency)}</td>
                          <td className="cell-num">{fmtMoney(it.deductions, it.currency)}</td>
                          <td className={`cell-num${it.netPay < 0 ? " pay-neg" : ""}`} style={{ fontWeight: 700 }}>{fmtMoney(it.netPay, it.currency)}</td>
                          {editable && !preview ? (
                            <td>
                              <button type="button" className="btn-md ghost" disabled={!!editRow}
                                aria-label={`${it.employeeName}: kalemi düzenle`} title="Kalemi düzenle"
                                onClick={() => setEditRow({
                                  id: it.id,
                                  grossPay: String(it.grossPay),
                                  allowances: String(it.allowances),
                                  deductions: String(it.deductions),
                                })}>
                                <Icon name="edit" size={14} />
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirm === "approve"}
        title="Bordroyu onayla"
        message={run ? `${run.code ?? "Bordro"} (${periodLabel(run.periodYear, run.periodMonth)}) onaylanacak. Onaydan sonra kalemler düzenlenemez.` : ""}
        confirmLabel="Onayla" tone="primary" busy={approveMut.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => run && approveMut.mutate({ id: run.id })}
      />
      <ConfirmDialog
        open={confirm === "delete"}
        title="Bordro dönemini sil"
        message={run ? `${run.code ?? "Bordro"} (${periodLabel(run.periodYear, run.periodMonth)}) ve tüm kalemleri kalıcı olarak silinecek. Bu işlem geri alınamaz.` : ""}
        confirmLabel="Sil" tone="danger" busy={removeMut.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => run && removeMut.mutate({ id: run.id })}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  // ── Mobil: kart görünümü (bordro dönemleri) ──
  if (isMobile) {
    const mb = (c: string) => (c === "b-warn" ? "b-warning" : c || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          {canWrite ? (
            <button type="button" className="btn-md primary sm" style={{ justifyContent: "center" }} onClick={openCreate}>
              <Icon name="plus" size={14} />Dönem Oluştur
            </button>
          ) : null}
        </div>

        {listQ.isError ? (
          <div className="m-empty">Bordro yüklenemedi: {listQ.error.message}</div>
        ) : listQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : runs.length === 0 ? (
          <div className="m-empty">
            {canWrite
              ? "\"Dönem Oluştur\" ile ilk bordro dönemini (BRD-YYYY-MM) başlatın."
              : "Görüntülenecek bordro dönemi yok."}
          </div>
        ) : (
          runs.map((r) => {
            const m = STATUS_META[r.status as PayrollStatus];
            return (
              <MCard pad key={r.id} onClick={() => setDetailId(r.id)} style={{ cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="mono" style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.code}</span>
                  <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--neutral-900)", marginTop: 6 }}>{periodLabel(r.periodYear, r.periodMonth)}</div>
                <div style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>{r.tesisName ?? "Global"}</div>
                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  <div style={{ flex: 1 }}><div className="m-ovl">Kalem</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.itemCount}</div></div>
                  <div style={{ flex: 1 }}><div className="m-ovl">Net Toplam</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{r.netTotal === null ? "karışık" : fmtMoney(r.netTotal, r.netTotals[0]?.currency)}</div></div>
                </div>
              </MCard>
            );
          })
        )}

        {runs.length > 0 ? (
          <div style={{ textAlign: "center", fontSize: 12, color: "var(--neutral-400)", marginTop: 2 }}>{runs.length} dönem</div>
        ) : null}

        {drawers}
      </>
    );
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, color: "var(--neutral-500)" }}>
          Aylık bordro dönemleri — brüt maaş, ek ödemeler ve kesintiler.
        </span>
        {canWrite ? (
          <button type="button" className="btn-md primary" onClick={openCreate}>
            <Icon name="plus" size={14} />Dönem Oluştur
          </button>
        ) : null}
      </div>

      <div className="dt2-wrap">
        <div className="dt2-scroll">
          {listQ.isError ? (
            <div className="form-banner-err">Bordro yüklenemedi: {listQ.error.message}</div>
          ) : listQ.isLoading ? (
            <div className="empty-state"><p className="sub">Yükleniyor…</p></div>
          ) : runs.length === 0 ? (
            <div className="empty-state">
              <span className="ic" aria-hidden="true"><Icon name="banknote" size={30} /></span>
              <p className="ttl">Henüz bordro dönemi yok</p>
              <p className="sub">
                {canWrite
                  ? "\"Dönem Oluştur\" ile ilk bordro dönemini (BRD-YYYY-MM) başlatın, sonra \"Hesapla\" ile personel kalemlerini üretin."
                  : "Görüntülenecek bordro dönemi yok."}
              </p>
            </div>
          ) : (
            <table className="dt2">
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Kod</th>
                  <th style={{ width: 160 }}>Dönem</th>
                  <th>Tesis</th>
                  <th style={{ width: 150 }}>Durum</th>
                  <th className="num" style={{ width: 90 }}>Kalem</th>
                  <th className="num" style={{ width: 180 }}>Net Toplam</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const m = STATUS_META[r.status as PayrollStatus];
                  return (
                    <tr key={r.id} onClick={() => setDetailId(r.id)} style={{ cursor: "pointer" }} title="Bordro detayını aç">
                      <td className="cell-id">{r.code}</td>
                      <td>{periodLabel(r.periodYear, r.periodMonth)}</td>
                      <td>{r.tesisName ?? <span style={{ color: "var(--neutral-400)" }}>Global</span>}</td>
                      <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                      <td className="cell-num">{r.itemCount}</td>
                      {/* NULL = birden çok para birimi → tek toplam basmak YALAN olur. */}
                      <td className="cell-num" title={r.currencyCount > 1 ? r.netTotals.map((t) => `${fmtNum(t.netTotal)} ${t.currency}`).join(" · ") : undefined}>
                        {r.netTotal === null ? "karışık" : fmtMoney(r.netTotal, r.netTotals[0]?.currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {runs.length > 0 ? (
          <div className="dt2-foot"><span>{runs.length} dönem</span></div>
        ) : null}
      </div>

      {drawers}
    </>
  );
}
